import { Document, Image as PDFImage, Page, pdf } from '@react-pdf/renderer';
import { DOCUMENT_TYPE } from 'doc-mate-store/lib/constants/loadDocument';
import { AdditionalBillingLeg } from 'doc-mate-store/lib/models/AdditionalBillingLeg';
import { LoadDocumentFile } from 'doc-mate-store/lib/models/LoadDocument';
import { OrderLeg } from 'doc-mate-store/lib/models/OrderLeg';
import { saveAs } from 'file-saver';
import { PDFDocument } from 'pdf-lib';

/**
 * Converts a list of photo objects into a PDF Blob using @react-pdf/renderer
 */
export async function renderPhotosToPdfBlob(photos: any[]): Promise<Blob> {
  const PDFPhotos = () => (
    <Document>
      {photos.map((photo: any) => (
        <Page
          key={photo.id}
          style={{ padding: 10 }}
          orientation={photo.width > photo.height ? 'landscape' : 'portrait'}
        >
          <PDFImage src={photo.src} />
        </Page>
      ))}
    </Document>
  );

  return await pdf(<PDFPhotos />).toBlob();
}

export async function appendSectionToPdf(
  mergedPdf: PDFDocument,
  photos: any[],
  files: LoadDocumentFile[],
): Promise<void> {
  if (photos && photos.length > 0) {
    const photosBlob = await renderPhotosToPdfBlob(photos);
    if (photosBlob) {
      const photosArrayBuffer = await photosBlob.arrayBuffer();
      const photosPdfDoc = await PDFDocument.load(photosArrayBuffer);
      const copiedPages = await mergedPdf.copyPages(
        photosPdfDoc,
        photosPdfDoc.getPageIndices(),
      );
      copiedPages.forEach(page => mergedPdf.addPage(page));
    }
  }

  if (files && files.length > 0) {
    for (const file of files) {
      try {
        const response = await fetch(file.file);
        const arrayBuffer = await response.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(
          pdfDoc,
          pdfDoc.getPageIndices(),
        );
        copiedPages.forEach(page => mergedPdf.addPage(page));
      } catch (error) {
        console.error(`Failed to merge file ${file.name}:`, error);
      }
    }
  }
}

/**
 * Generates and downloads the Core Paperwork PDF (Rate Con, BOL, Additional Docs)
 */
export async function downloadCoreDocsPdf(
  load: OrderLeg,
  as400ManifestNumber?: string,
  includeRateCon: boolean = true,
): Promise<{ success: boolean; empty?: boolean }> {
  // Collect document items directly from the OrderLeg model
  const photosRateCon = load.getDocumentForCarousel(DOCUMENT_TYPE.RATE_CON);
  const filesRateCon = load.getDocumentForFiles(DOCUMENT_TYPE.RATE_CON);

  const photosBillOfLading = load.getDocumentForCarousel(
    DOCUMENT_TYPE.BILL_OF_LADING,
  );
  const filesBillOfLading = load.getDocumentForFiles(
    DOCUMENT_TYPE.BILL_OF_LADING,
  );

  const photosAdditionalDocument = load.getDocumentForCarousel(
    DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS,
  );
  const filesAdditionalDocument = load.getDocumentForFiles(
    DOCUMENT_TYPE.ADDITIONAL_DOCUMENTS,
  );

  const mergedPdf = await PDFDocument.create();

  // Determine file name matching LoadDocument's convention
  const manifestNumber = as400ManifestNumber || load.id;
  const pdfFileName = `${manifestNumber}.pdf`;

  // 1. Rate Con (Dispatcher or Billing only)
  if (includeRateCon) {
    await appendSectionToPdf(mergedPdf, photosRateCon, filesRateCon);
  }

  // 2. Bill of Lading
  await appendSectionToPdf(mergedPdf, photosBillOfLading, filesBillOfLading);

  // 3. Additional Documents
  await appendSectionToPdf(
    mergedPdf,
    photosAdditionalDocument,
    filesAdditionalDocument,
  );

  // Guard against downloading empty files
  if (mergedPdf.getPageCount() === 0) {
    return { success: false, empty: true };
  }

  const mergedPdfBytes = await mergedPdf.save();
  const finalBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
  saveAs(finalBlob, pdfFileName);

  return { success: true };
}

/**
 * Generates and downloads the Additional Billing paperwork PDF
 */
export async function downloadAdditionalBillingPdf(
  billingLeg: AdditionalBillingLeg,
  as400ManifestNumber?: string,
): Promise<{ success: boolean; empty?: boolean }> {
  // Resilient document resolution across naming variations
  const primaryDocument =
    billingLeg.getDocument('additional_documents') ||
    billingLeg.getDocument('additional_document') ||
    billingLeg.getDocument('additional_billing') ||
    Array.from(billingLeg.documents)[0];

  if (!primaryDocument) {
    return { success: false, empty: true };
  }

  const photos = (primaryDocument.photos || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(p => ({
      id: p.id,
      src: p.photo?.fullSize || '',
      thumbnail: p.photo?.thumbnail || '',
      width: p.width || 0,
      height: p.height || 0,
      rotate: p.rotate || 0,
    }));

  const files = (primaryDocument.files || [])
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const mergedPdf = await PDFDocument.create();
  const pdfFileName = `${as400ManifestNumber ||
    billingLeg.dmId ||
    billingLeg.id}.pdf`;

  await appendSectionToPdf(mergedPdf, photos, files);

  if (mergedPdf.getPageCount() === 0) {
    return { success: false, empty: true };
  }

  const mergedPdfBytes = await mergedPdf.save();
  const finalBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
  saveAs(finalBlob, pdfFileName);

  return { success: true };
}
